import { collection, doc, addDoc, updateDoc, getDoc, query, where, getDocs, arrayUnion, serverTimestamp } from 'firebase/firestore'
import { db } from './config'
import { CollaboratorInvite } from '../../types'

/**
 * Send a collaborator invite via email
 * Note: This is a placeholder. In production, you'd use Firebase Functions
 * or a backend service to send actual emails.
 */
export const sendCollaboratorInvite = async (
  projectId: string,
  projectTitle: string,
  invitedByEmail: string,
  inviteeEmail: string,
  role: 'editor' | 'viewer' = 'editor'
): Promise<string> => {
  try {
    // Create invite document
    const inviteData = {
      projectId,
      projectTitle,
      invitedBy: invitedByEmail,
      email: inviteeEmail,
      role,
      status: 'pending',
      invitedAt: new Date().toISOString(),
      createdAt: serverTimestamp()
    }

    const docRef = await addDoc(collection(db, 'invites'), inviteData)
    
    // Add invite to project
    const projectRef = doc(db, 'projects', projectId)
    await updateDoc(projectRef, {
      collaboratorInvites: arrayUnion({
        id: docRef.id,
        email: inviteeEmail,
        role,
        status: 'pending',
        invitedAt: new Date().toISOString(),
        invitedBy: invitedByEmail
      })
    })

    // TODO: In production, trigger email via Firebase Functions or backend API
    console.log('Invite created:', {
      inviteId: docRef.id,
      to: inviteeEmail,
      projectTitle,
      from: invitedByEmail
    })

    // For now, we'll just create the invite document
    // In production, you'd call a Cloud Function here:
    // await httpsCallable(functions, 'sendInviteEmail')({ inviteId: docRef.id })

    return docRef.id
  } catch (error) {
    console.error('Failed to send invite:', error)
    throw error
  }
}

/**
 * Accept a collaborator invite
 */
export const acceptInvite = async (
  inviteId: string,
  userId: string,
  userEmail: string
): Promise<void> => {
  try {
    // Get invite
    const inviteRef = doc(db, 'invites', inviteId)
    const inviteSnap = await getDoc(inviteRef)
    
    if (!inviteSnap.exists()) {
      throw new Error('Invite not found')
    }

    const invite = inviteSnap.data()
    
    if (invite.email !== userEmail) {
      throw new Error('This invite is not for you')
    }

    if (invite.status !== 'pending') {
      throw new Error('Invite has already been processed')
    }

    // Update invite status
    await updateDoc(inviteRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp(),
      acceptedBy: userId
    })

    // Add user to project's sharedWith
    const projectRef = doc(db, 'projects', invite.projectId)
    await updateDoc(projectRef, {
      sharedWith: arrayUnion({
        id: userId,
        email: userEmail,
        role: invite.role
      }),
      updatedAt: serverTimestamp()
    })

    // Update the invite in the project's collaboratorInvites array
    const projectSnap = await getDoc(projectRef)
    if (projectSnap.exists()) {
      const projectData = projectSnap.data()
      const collaboratorInvites = projectData.collaboratorInvites || []
      const updatedInvites = collaboratorInvites.map((inv: CollaboratorInvite) =>
        inv.id === inviteId ? { ...inv, status: 'accepted' as const } : inv
      )
      await updateDoc(projectRef, {
        collaboratorInvites: updatedInvites
      })
    }
  } catch (error) {
    console.error('Failed to accept invite:', error)
    throw error
  }
}

/**
 * Decline a collaborator invite
 */
export const declineInvite = async (
  inviteId: string,
  userEmail: string
): Promise<void> => {
  try {
    const inviteRef = doc(db, 'invites', inviteId)
    const inviteSnap = await getDoc(inviteRef)
    
    if (!inviteSnap.exists()) {
      throw new Error('Invite not found')
    }

    const invite = inviteSnap.data()
    
    if (invite.email !== userEmail) {
      throw new Error('This invite is not for you')
    }

    await updateDoc(inviteRef, {
      status: 'declined',
      declinedAt: serverTimestamp()
    })

    // Update the invite in the project's collaboratorInvites array
    const projectRef = doc(db, 'projects', invite.projectId)
    const projectSnap = await getDoc(projectRef)
    if (projectSnap.exists()) {
      const projectData = projectSnap.data()
      const collaboratorInvites = projectData.collaboratorInvites || []
      const updatedInvites = collaboratorInvites.map((inv: CollaboratorInvite) =>
        inv.id === inviteId ? { ...inv, status: 'declined' as const } : inv
      )
      await updateDoc(projectRef, {
        collaboratorInvites: updatedInvites
      })
    }
  } catch (error) {
    console.error('Failed to decline invite:', error)
    throw error
  }
}

/**
 * Get pending invites for a user by email
 */
export const getPendingInvites = async (userEmail: string): Promise<CollaboratorInvite[]> => {
  try {
    const q = query(
      collection(db, 'invites'),
      where('email', '==', userEmail),
      where('status', '==', 'pending')
    )
    
    const snapshot = await getDocs(q)
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as CollaboratorInvite))
  } catch (error) {
    console.error('Failed to get pending invites:', error)
    return []
  }
}

/**
 * Remove a collaborator from a project
 */
export const removeCollaborator = async (
  projectId: string,
  userId: string
): Promise<void> => {
  try {
    const projectRef = doc(db, 'projects', projectId)
    const projectSnap = await getDoc(projectRef)
    
    if (!projectSnap.exists()) {
      throw new Error('Project not found')
    }

    const projectData = projectSnap.data()
    const sharedWith = projectData.sharedWith || []
    const updatedSharedWith = sharedWith.filter((user: any) => user.id !== userId)

    await updateDoc(projectRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Failed to remove collaborator:', error)
    throw error
  }
}

